import { Route, Routes } from "react-router-dom";
import { Fragment, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Toaster } from "sonner";

import GlobalStyles from "./styles/globalStyles";
import "./styles/index.css";

import HomePage from "./screens/HomePage/HomePage.js";

import { lineSpinner } from "ldrs";
import { refresh } from "./store/slices/auth/asyncThunks.js";
import { setLoggedIn } from "./store/slices/auth/slice.js";
import CartPage from "./screens/CartPage/CartPage.js";
import CoursesPage from "./screens/CoursesPage/CoursesPage.js";
import CourseDetailsPage from "./screens/CourseDetailsPage/CourseDetailsPage.js";
import AuthPage from "./screens/AuthPage/AuthPage.js";
import AdminPanel from "./screens/Admin/AdminPanel/AdminPanel.js";
import VideoPage from "./screens/User/VideoPage/VideoPage.js";
import MyCoursesPage from "./screens/User/MyCoursesPage/MyCoursesPage.js";
import ProfilePage from "./screens/User/ProfilePage/ProfilePage.js";
import UserLayout from "./components/UserLayout/UserLayout.js";
import GalleryPage from "./screens/GalleryPage/GalleryPage.js";
import AboutPage from "./screens/AboutPage/AboutPage.js";
import ContactPage from "./screens/ContactPage/ContactPage.js";
import CheckoutPage from "./screens/CheckoutPage/CheckoutPage.js";
import TermsOfServicePage from "./screens/TermsOfServicePage/TermsOfServicePage.js";
import PrivacyPolicyPage from "./screens/PrivacyPolicyPage/PrivacyPolicyPage.js";
import PasswordResetPage from "./screens/PasswordResetPage/PasswordResetPage.js";
import EmailVerificationPage from "./screens/EmailVerificationPage/EmailVerificationPage.js";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute.js";
import AdminLayout from "./components/AdminLayout/AdminLayout.js";
import AdminCourses from "./screens/Admin/AdminCourses/AdminCourses.js";
import AdminUsers from "./screens/Admin/AdminUsers/AdminUsers.js";
import AdminOrders from "./screens/Admin/AdminOrders/AdminOrders.js";
import AdminCertificates from "./screens/Admin/AdminCertificates/AdminCertificates.js";
import FAQPage from "./screens/FAQPage/FAQPage.js";
import PaymentSuccessPage from "./screens/PaymentPages/PaymentSuccessPage.js";
import PaymentFailedPage from "./screens/PaymentPages/PaymentFailedPage.js";
import AdminPromoEmailSender from "./screens/Admin/AdminPromoEmailSender/AdminPromoEmailSender.js";
import AdminPromoCodes from "./screens/Admin/AdminPromoCodes/AdminPromoCodes.js";
import AdminSettings from "./screens/Admin/AdminSettings/AdminSettings.js";
import AdminUserDetails from "./screens/Admin/AdminUserDetails/AdminUserDetails.js";
import AdminOrderDetails from "./screens/Admin/AdminOrderDetails/AdminOrderDetails.js";
import AdminCourseDetails from "./screens/Admin/AdminCourseDetails/AdminCourseDetails.js";
import AdminCertificateReview from "./screens/Admin/AdminCertificateReview/AdminCertificateReview.js";
import NotFoundPage from "./screens/NotFoundPage/NotFoundPage.js";

lineSpinner.register();

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(refresh())
      .unwrap()
      .then(() => {})
      .catch(() => {
        dispatch(setLoggedIn({ loggedIn: false }));
      });
  }, [dispatch]);

  return (
    <Fragment>
      <GlobalStyles />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/galerija" element={<GalleryPage />} />
        <Route path="/o-meni" element={<AboutPage />} />
        <Route path="/kontakt" element={<ContactPage />} />
        <Route path="/korpa" element={<CartPage />} />
        <Route path="/kursevi" element={<CoursesPage />} />
        <Route path="/kurs/:courseId" element={<CourseDetailsPage />} />
        <Route path="/pristup" element={<AuthPage />} />
        <Route path="/placanje" element={<CheckoutPage />} />

        <Route path="/uspesna-porudzbina" element={<PaymentSuccessPage />} />
        <Route path="/neuspesna-porudzbina" element={<PaymentFailedPage />} />

        <Route path="/uslovi-koriscenja" element={<TermsOfServicePage />} />
        <Route path="/politika-privatnosti" element={<PrivacyPolicyPage />} />
        <Route path="/cesta-pitanja" element={<FAQPage />} />
        <Route path="/resetovanje-lozinke" element={<PasswordResetPage />} />
        <Route path="/verifikacija" element={<EmailVerificationPage />} />

        <Route
          path="/admin-panel"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminPanel />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel/kursevi"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminCourses />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel/korisnici"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminUsers />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel/porudzbine"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminOrders />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel/sertifikati"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminCertificates />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel/promo-kodovi"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminPromoCodes />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel/promo-emails"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminPromoEmailSender />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel/podesavanja"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminSettings />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel/korisnici/:userId"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminUserDetails />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel/porudzbine/:orderId"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminOrderDetails />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel/kursevi/:courseId"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminCourseDetails />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel/sertifikati/:applicationId"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout>
                <AdminCertificateReview />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/korisnicki-panel/kursevi"
          element={
            <ProtectedRoute userOnly={true}>
              <UserLayout>
                <MyCoursesPage />
              </UserLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/korisnicki-panel/kursevi/:courseId/lekcije/:lessonId"
          element={
            <ProtectedRoute userOnly={true}>
              <UserLayout>
                <VideoPage />
              </UserLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/korisnicki-panel/profil"
          element={
            <ProtectedRoute userOnly={true}>
              <UserLayout>
                <ProfilePage />
              </UserLayout>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Toaster
        position="bottom-right"
        closeButton={false}
        richColors
        theme="light"
        toastOptions={{
          style: {
            background: "#ffffff",
            border: "1px solid #7C0E33",
            color: "#2c2c2c",
            borderRadius: "20px",
            fontFamily: "Source Code Pro, monospace",
            fontSize: "14px",
            fontWeight: "500",
            padding: "16px 20px",
            boxShadow: "var(--shadow-soft)",
          },
          duration: 4000,
        }}
      />
    </Fragment>
  );
}

export default App;
